<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher, onMount } from 'svelte';
	import { Button, Modal } from '@Shelly/shelly-design-system';
	import { energyPriceColorSchemes, type ColorSchemeKey } from '../../utils/colorSchemes';
	import EnergyPriceOptions from '../../components/options/EnergyPriceOptions.svelte';
	import BenchmarkTariffInfo from '../../components/benchmark/BenchmarkTariffInfo.svelte';
	import { buildVerivoxRedirectUrl } from '../../api/verivoxRedirect';
	import { energyPriceApi } from '../../api/verivoxApi';
	import { openExternalLink } from 'src/helpers/utilities';
	import type { EnergyTariff } from 'src/types/EnergyTariff';
	import VerivoxAttribution from '../../verivox/VerivoxAttribution.svelte';
	import { transformOfferToTariff } from '../../utils/tariffTransformers';
	import { createFunnel } from 'src/helpers/funnel_tracker';

	const verivoxFunnel = createFunnel('verivox_setup');

	export let showModal: boolean = false;
	export let postalCode: string = '';
	export let locationId: string | null = null;
	export let totalUsage: number = 2500;
	export let subPartnerId: string = '5762';
	export let campaignId: string = '2';

	let currentScheme: ColorSchemeKey = 'scheme1';
	let gradientStyle: string = '';
	let benchmarkTariff: EnergyTariff | null = null;
	let isLoadingBenchmarkTariff = false;

	const dispatch = createEventDispatcher();

	const fetchBenchmarkTariff = async () => {
		if (!postalCode || !locationId) {
			benchmarkTariff = null;
			return;
		}

		isLoadingBenchmarkTariff = true;

		try {
			const res = await energyPriceApi.fetchTariffs(postalCode, locationId);

			if (res?.isok && res?.data?.benchmarkTariff) {
				benchmarkTariff = transformOfferToTariff(res.data.benchmarkTariff);
			} else {
				benchmarkTariff = null;
			}
		} catch (err) {
			console.error('Error fetching benchmark tariff:', err);
			benchmarkTariff = null;
		} finally {
			isLoadingBenchmarkTariff = false;
		}
	};

	onMount(() => {
		currentScheme = energyPriceColorSchemes.getCurrentScheme();
		gradientStyle = energyPriceColorSchemes.schemes[currentScheme];
		if (postalCode && locationId) {
			fetchBenchmarkTariff();
		}
	});

	$: if (currentScheme) {
		gradientStyle = energyPriceColorSchemes.schemes[currentScheme];
	}

	$: if (postalCode && locationId && showModal) {
		fetchBenchmarkTariff();
		verivoxFunnel.trackStep('tariffs');
	}

	const handleClose = () => {
		showModal = false;
		dispatch('close');
	};

	const handleExploreMore = () => {
		verivoxFunnel.incrementCounter('explore_more_clicks');
		if (postalCode && locationId) {
			// Use cached consumption if totalUsage is default, otherwise use provided totalUsage
			const cachedConsumption = energyPriceApi.getCachedConsumption();
			const usageToUse =
				totalUsage === 2500 && cachedConsumption !== null ? cachedConsumption : totalUsage;

			const redirectUrl = buildVerivoxRedirectUrl(
				postalCode,
				locationId,
				usageToUse,
				subPartnerId,
				campaignId,
			);
			openExternalLink(redirectUrl);
		} else {
			openExternalLink('https://www.verivox.de/?tunnel-partner5762');
		}
	};
</script>

<Modal
	{showModal}
	on:close={handleClose}
	title={$format('energy_price_best_comparable_prices', {
		default: 'Günstige Stromtarife entdecken',
		locale: 'de',
	})}
	backdropClose={true}
>
	<!-- <div slot="headerActions">
		<BenchmarkTariffInfoButton {benchmarkTariff} isLoading={isLoadingBenchmarkTariff} />
	</div> -->
	<svelte:fragment slot="content">
		<div class="energy-tariffs-modal-content" style="--energy-price-gradient: {gradientStyle}">
			{#if benchmarkTariff}
				<BenchmarkTariffInfo {benchmarkTariff} />
			{/if}
			<EnergyPriceOptions
				{postalCode}
				{locationId}
				variant="light"
				maxTariffs={5}
				{totalUsage}
				{subPartnerId}
				{campaignId}
			/>
		</div>
	</svelte:fragment>
	<div slot="footer" class="modal-footer-content">
		<div class="d-flex justify-content-end gap-s">
			<Button
				variant="primary"
				class="width-md-auto width-sm-full"
				size="m"
				on:click={handleExploreMore}
			>
				{$format('button_explore_more', {
					default: 'Mehr Tarife vergleichen',
					locale: 'de',
				})}
			</Button>
			<Button
				class="width-md-auto width-sm-full"
				size="m"
				variant="secondary"
				on:click={handleClose}
			>
				{$format('button_close', { default: 'Close' })}
			</Button>
		</div>
		<div class="footer-attribution">
			<VerivoxAttribution />
		</div>
	</div>
</Modal>

<style>
	:global(.energy-tariffs-modal-content .energy-tariffs-modal-wrapper .price-option.variant-light) {
		background: var(--energy-price-gradient) !important;
		backdrop-filter: none !important;
		border: 1px solid rgba(255, 255, 255, 0.2) !important;
	}

	:global(
		.energy-tariffs-modal-content .energy-tariffs-modal-wrapper .price-option.variant-light:hover
	) {
		background: var(--energy-price-gradient) !important;
		border: 1px solid rgba(255, 255, 255, 0.3) !important;
	}

	:global(
		.energy-tariffs-modal-content .energy-tariffs-modal-wrapper .price-option.variant-light.best
	) {
		background: var(--energy-price-gradient) !important;
		border: 2px solid rgba(255, 255, 255, 0.4) !important;
	}

	:global(
		.energy-tariffs-modal-content
			.energy-tariffs-modal-wrapper
			.price-option.variant-light
			.provider-name
	),
	:global(
		.energy-tariffs-modal-content
			.energy-tariffs-modal-wrapper
			.price-option.variant-light
			.tariff-name
	),
	:global(
		.energy-tariffs-modal-content
			.energy-tariffs-modal-wrapper
			.price-option.variant-light
			.option-price
	),
	:global(
		.energy-tariffs-modal-content
			.energy-tariffs-modal-wrapper
			.price-option.variant-light
			.total-costs
	),
	:global(
		.energy-tariffs-modal-content
			.energy-tariffs-modal-wrapper
			.price-option.variant-light
			.detail-item
	) {
		color: #ffffff !important;
	}

	.modal-footer-content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s);
		width: 100%;
	}

	.footer-attribution {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-top: var(--spacing-xs);
	}

	:global(.attribution-text) {
		opacity: 0.8;
		text-align: center;
		color: var(--color-text-secondary, #6b7280);
		font-size: 11px;
		line-height: 1.4;
	}

	.gap-s {
		gap: var(--spacing-s);
	}
</style>
